import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  FileButton,
  Group,
  Image,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconPhoto, IconTrash } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import { toErrorMessage } from '@util/toErrorMessage';

export default function AchievementAssetsPanel({
  achievementId,
  assets = [],
  readOnly = false,
  onChanged = () => {},
}) {
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const images = (assets || []).filter(
    (asset) => asset.variety === 1 || asset.variety_formatted === 'image',
  );

  function onUploadImage(file) {
    if (!file || !achievementId || readOnly) return;

    const formData = new FormData();
    formData.append('tempfile', file);
    formData.append('achievement_id', achievementId);
    formData.append('variety', '1');

    setUploading(true);
    axios
      .post('/assets/', formData)
      .then(() => {
        toast.success('Trophy image uploaded');
        onChanged();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setUploading(false));
  }

  function onRemove(assetId) {
    if (!assetId || readOnly) return;
    setRemovingId(assetId);
    axios
      .post(`/assets/${assetId}/remove/`, {})
      .then(() => {
        toast.success('Removed');
        onChanged();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setRemovingId(null));
  }

  return (
    <Paper p="md" withBorder radius="sm">
      <Stack gap="md">
        <div>
          <Text fw={700}>Trophy image</Text>
          <Text size="sm" c="dimmed">
            PNG or other image shown in the athlete Trophy Case.
          </Text>
        </div>

        {images.length === 0 ? (
          <Text size="sm" c="dimmed">
            No trophy image yet.
          </Text>
        ) : (
          <Stack gap="sm">
            {images.map((asset) => (
              <Group
                key={asset.id}
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
              >
                <Group align="flex-start" gap="sm" style={{ flex: 1, minWidth: 0 }}>
                  {asset.filename_url ? (
                    <Image
                      src={asset.filename_url}
                      alt={asset.original_filename || 'Trophy'}
                      w={96}
                      h={96}
                      radius="sm"
                      fit="cover"
                    />
                  ) : (
                    <Badge size="sm" variant="light">
                      image
                    </Badge>
                  )}
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" c="dimmed" truncate>
                      {asset.original_filename || asset.content_type || 'Uploaded file'}
                    </Text>
                  </Stack>
                </Group>
                {!readOnly && (
                  <BasicButton
                    text="Remove"
                    onClick={() => onRemove(asset.id)}
                    loading={removingId === asset.id}
                    disabled={Boolean(removingId) || uploading}
                    color="red"
                    variant="outline"
                    leftSection={<IconTrash size={14} />}
                  />
                )}
              </Group>
            ))}
          </Stack>
        )}

        {!readOnly && (
          <Group>
            <FileButton
              onChange={onUploadImage}
              accept="image/png,image/jpeg,image/webp,image/gif"
              disabled={uploading}
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  leftSection={<IconPhoto size={14} />}
                  loading={uploading}
                  disabled={uploading}
                >
                  Upload PNG
                </Button>
              )}
            </FileButton>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
